'use client';

import { useEffect, useRef, useState } from 'react';

interface LazyVideoSource {
  src: string;
  type: string;
}

interface LazyVideoProps {
  /** Poster image — shown as a plain <img> until the video mounts. */
  poster: string;
  /** One or more <source> entries (webm first, mp4 fallback). */
  sources: LazyVideoSource[];
  ariaLabel: string;
  /** Class for both the <video> and the poster <img> (usually the fill class). */
  className?: string;
  /** Optional distinct class for the poster <img> (defaults to `className`). */
  posterClassName?: string;
  /** How far ahead of the viewport to start loading. Default 400px. */
  rootMargin?: string;
}

/**
 * Below-the-fold autoplay video that does NOT download until it approaches
 * the viewport. Until then it renders only the (cheap) poster image.
 *
 * Why: several landing sections autoplay videos. When those `<video>` tags are
 * in the DOM at first paint, the browser eagerly fetches them (autoplay forces
 * a load even with `preload="metadata"`) — on the homepage that was ~1.7 MB of
 * section videos competing with the hero LCP over a throttled connection and
 * pushing mobile LCP to ~7 s. Gating the mount behind an IntersectionObserver
 * frees the critical-path bandwidth for the hero. Mirrors the pattern already
 * used in SignupVideoSection / DemoVideoSection, extracted so it's reusable.
 */
export function LazyVideo({
  poster,
  sources,
  ariaLabel,
  className = '',
  posterClassName,
  rootMargin = '400px 0px',
}: LazyVideoProps) {
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (mounted) return;
    if (typeof IntersectionObserver === 'undefined') {
      // Legit: no IO support → mount immediately (SSR/old browsers).
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setMounted(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setMounted(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );
    if (sentinelRef.current) observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [mounted, rootMargin]);

  return (
    <>
      <div
        ref={sentinelRef}
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-px"
      />
      {mounted ? (
        <video
          className={className}
          poster={poster}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-label={ariaLabel}
        >
          {sources.map((s) => (
            <source key={s.src} src={s.src} type={s.type} />
          ))}
        </video>
      ) : (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={poster}
          alt=""
          aria-hidden="true"
          className={posterClassName || className}
          // eager: das Poster IST der Fallback — lazy ließ auf langsamen
          // Verbindungen weiße Löcher in der Demo-Sektion (Review 2026-07-31);
          // das schwere Video bleibt weiterhin IO-gegated.
          loading="eager"
          decoding="async"
        />
      )}
    </>
  );
}
